import type { AxiosInstance } from 'axios';
import { getApiClient, logApiError } from './apiClient';

// Document interfaces
export interface Document {
    _id: string;
    fileName: string;
    originalName: string;
    mimeType: string;
    size: number;
    path: string;
    category: DocumentCategory;
    subcategory?: string;
    entityType: EntityType;
    entityId: string;
    description?: string;
    tags: string[];
    metadata: Record<string, any>;
    accessLevel: AccessLevel;
    permissions: DocumentPermission[];
    version: number;
    parentDocumentId?: string;
    isArchived: boolean;
    archivedAt?: Date;
    expiresAt?: Date;
    uploadedBy: string;
    uploadedAt: string;
    lastAccessedAt?: string;
    downloadCount: number;
    checksum: string;
    virusScanStatus: VirusScanStatus;
    virusScanResult?: string;
    virusScanDate?: string;
    createdAt: string;
    updatedAt: string;
    url: string;
    id: string;
}

export interface DocumentPermission {
    userId?: string;
    role?: string;
    permissions: ('READ' | 'WRITE' | 'DELETE' | 'SHARE')[];
}

export enum DocumentCategory {
    KYC = 'KYC',
    INVOICE = 'INVOICE',
    SHIPPING_LABEL = 'SHIPPING_LABEL',
    AUTHORIZATION = 'AUTHORIZATION',
    PROOF_OF_DELIVERY = 'PROOF_OF_DELIVERY',
    CUSTOMS = 'CUSTOMS',
    MANIFEST = 'MANIFEST',
    BILLING = 'BILLING',
    CONTRACT = 'CONTRACT',
    CERTIFICATE = 'CERTIFICATE',
    REPORT = 'REPORT',
    OTHER = 'OTHER'
}

export enum EntityType {
    AWB = 'AWB',
    MANIFEST = 'MANIFEST',
    CUSTOMER = 'CUSTOMER',
    VENDOR = 'VENDOR',
    USER = 'USER'
}

export enum AccessLevel {
    PUBLIC = 'PUBLIC',
    INTERNAL = 'INTERNAL',
    RESTRICTED = 'RESTRICTED',
    CONFIDENTIAL = 'CONFIDENTIAL'
}

export enum VirusScanStatus {
    PENDING = 'PENDING',
    CLEAN = 'CLEAN',
    INFECTED = 'INFECTED',
    QUARANTINED = 'QUARANTINED',
    SKIPPED = 'SKIPPED'
}

export interface UploadDocumentRequest {
    file: File;
    category: DocumentCategory;
    subcategory?: string;
    entityType: EntityType;
    entityId: string;
    description?: string;
    tags?: string[];
    metadata?: Record<string, any>;
    accessLevel?: AccessLevel;
    expiresAt?: Date;
}

export interface DocumentSearchQuery {
    entityType?: EntityType;
    entityId?: string;
    category?: DocumentCategory;
    subcategory?: string;
    tags?: string[];
    uploadedBy?: string;
    accessLevel?: AccessLevel;
    isArchived?: boolean;
    virusScanStatus?: VirusScanStatus;
    dateFrom?: Date;
    dateTo?: Date;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface DocumentSearchResult {
    documents: Document[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface BulkOperationRequest {
    documentIds: string[];
    operation: 'archive' | 'unarchive' | 'delete';
}

export class DocumentService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = getApiClient({ useWindowInstance: true });
    }

    /**
     * Upload a single document
     */
    async uploadDocument(request: UploadDocumentRequest): Promise<Document> {
        try {
            const formData = new FormData();
            formData.append('file', request.file);
            formData.append('category', request.category);
            formData.append('entityType', request.entityType);
            formData.append('entityId', request.entityId);

            if (request.subcategory) formData.append('subcategory', request.subcategory);
            if (request.description) formData.append('description', request.description);
            if (request.tags) formData.append('tags', JSON.stringify(request.tags));
            if (request.metadata) formData.append('metadata', JSON.stringify(request.metadata));
            if (request.accessLevel) formData.append('accessLevel', request.accessLevel);
            if (request.expiresAt) formData.append('expiresAt', request.expiresAt.toISOString());

            const response = await this.axiosInstance.post('/api/documents/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Upload Document Error', error);
            throw error;
        }
    }

    /**
     * Upload multiple documents
     */
    async uploadMultipleDocuments(requests: UploadDocumentRequest[]): Promise<{
        success: Document[];
        failed: { filename: string; error: string; }[];
    }> {
        try {
            const formData = new FormData();

            requests.forEach((request, index) => {
                formData.append('files', request.file);
                formData.append(`category_${index}`, request.category);
                formData.append(`entityType_${index}`, request.entityType);
                formData.append(`entityId_${index}`, request.entityId);

                if (request.subcategory) formData.append(`subcategory_${index}`, request.subcategory);
                if (request.description) formData.append(`description_${index}`, request.description);
                if (request.tags) formData.append(`tags_${index}`, JSON.stringify(request.tags));
                if (request.metadata) formData.append(`metadata_${index}`, JSON.stringify(request.metadata));
                if (request.accessLevel) formData.append(`accessLevel_${index}`, request.accessLevel);
                if (request.expiresAt) formData.append(`expiresAt_${index}`, request.expiresAt.toISOString());
            });

            const response = await this.axiosInstance.post('/api/documents/upload/multiple', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Upload Multiple Documents Error', error);
            throw error;
        }
    }

    /**
     * Get document by ID
     */
    async getDocument(documentId: string): Promise<Document> {
        try {
            const response = await this.axiosInstance.get(`/api/documents/${documentId}`);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Get Document Error', error);
            throw error;
        }
    }

    /**
     * Download document
     */
    async downloadDocument(documentId: string, originalName?: string): Promise<void> {
        try {
            const response = await this.axiosInstance.get(`/api/documents/${documentId}/download`, {
                responseType: 'blob',
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', originalName || `document_${documentId}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            logApiError('Document Service - Download Document Error', error);
            throw error;
        }
    }

    /**
     * Get document URL for viewing
     */
    async getDocumentUrl(documentId: string): Promise<string> {
        try {
            const response = await this.axiosInstance.get(`/api/documents/${documentId}/url`);
            return response.data.data.url;
        } catch (error: any) {
            logApiError('Document Service - Get Document URL Error', error);
            throw error;
        }
    }

    /**
     * Search documents
     */
    async searchDocuments(query: DocumentSearchQuery): Promise<DocumentSearchResult> {
        try {
            const params = new URLSearchParams();

            if (query.entityType) params.append('entityType', query.entityType);
            if (query.entityId) params.append('entityId', query.entityId);
            if (query.category) params.append('category', query.category);
            if (query.subcategory) params.append('subcategory', query.subcategory);
            if (query.uploadedBy) params.append('uploadedBy', query.uploadedBy);
            if (query.accessLevel) params.append('accessLevel', query.accessLevel);
            if (query.isArchived !== undefined) params.append('isArchived', query.isArchived.toString());
            if (query.virusScanStatus) params.append('virusScanStatus', query.virusScanStatus);
            if (query.dateFrom) params.append('dateFrom', query.dateFrom.toISOString());
            if (query.dateTo) params.append('dateTo', query.dateTo.toISOString());
            if (query.search) params.append('search', query.search);
            if (query.page) params.append('page', query.page.toString());
            if (query.limit) params.append('limit', query.limit.toString());
            if (query.sortBy) params.append('sortBy', query.sortBy);
            if (query.sortOrder) params.append('sortOrder', query.sortOrder);
            if (query.tags && query.tags.length > 0) params.append('tags', query.tags.join(','));

            const response = await this.axiosInstance.get(`/api/documents/search?${params.toString()}`);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Search Documents Error', error);
            throw error;
        }
    }

    /**
     * Get documents by entity
     */
    async getDocumentsByEntity(entityType: EntityType, entityId: string): Promise<Document[]> {
        try {
            const apiUrl = `/api/documents/entity/${entityType}/${entityId}`;
            const response = await this.axiosInstance.get(apiUrl);

            if (response.data && response.data.success === false) {
                return [];
            }
            return response.data?.data ?? [];
        } catch (error: any) {
            logApiError('Document Service - Get Documents By Entity Error', error);
            throw error;
        }
    }

    /**
     * Update document
     */
    async updateDocument(documentId: string, updates: Partial<Document>): Promise<Document> {
        try {
            const response = await this.axiosInstance.put(`/api/documents/${documentId}`, updates);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Update Document Error', error);
            throw error;
        }
    }

    /**
     * Archive document
     */
    async archiveDocument(documentId: string): Promise<Document> {
        try {
            const response = await this.axiosInstance.post(`/api/documents/${documentId}/archive`);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Archive Document Error', error);
            throw error;
        }
    }

    /**
     * Unarchive document
     */
    async unarchiveDocument(documentId: string): Promise<Document> {
        try {
            const response = await this.axiosInstance.post(`/api/documents/${documentId}/unarchive`);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Unarchive Document Error', error);
            throw error;
        }
    }

    /**
     * Delete document
     */
    async deleteDocument(documentId: string): Promise<void> {
        try {
            await this.axiosInstance.delete(`/api/documents/${documentId}`);
        } catch (error: any) {
            logApiError('Document Service - Delete Document Error', error);
            throw error;
        }
    }

    /**
     * Bulk operations
     */
    async bulkOperation(request: BulkOperationRequest): Promise<{
        success: string[];
        failed: { documentId: string; error: string; }[];
    }> {
        try {
            const response = await this.axiosInstance.post('/api/documents/bulk', request);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Bulk Operation Error', error);
            throw error;
        }
    }

    /**
     * Get validation config for a category
     */
    async getValidationConfig(category: DocumentCategory): Promise<any> {
        try {
            const response = await this.axiosInstance.get(`/api/documents/validation-config/${category}`);
            return response.data.data;
        } catch (error: any) {
            logApiError('Document Service - Get Validation Config Error', error);
            throw error;
        }
    }

    /**
     * Generate all required documents for an AWB
     */
    async generateAllAwbDocuments(awbNumber: string): Promise<Document[]> {
        try {
            const response = await this.axiosInstance.post(`/api/documents/generate/awb/${awbNumber}/all`);
            if (response.data && response.data.success === false) {
                return [];
            }
            return response.data?.data ?? [];
        } catch (error: any) {
            logApiError('Document Service - Generate AWB Documents Error', error);
            throw error;
        }
    }
}

// Export singleton instance
export const documentService = new DocumentService(); 
